import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/InfoPages.css';

const Privacy = () => {
  return (
    <div className="info-page-container">
      <div className="info-page">
        <h1>Privacy Policy</h1>
        <p className="effective-date">Effective Date: December 26, 2025</p>

        <p>Mirador Pacific ("we," "us," or "our") operates Artifex (the "Platform"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use Artifex.</p>

        <h2>1. Information We Collect</h2>

        <h3>1.1 Information You Provide to Us</h3>
        <ul>
          <li><strong>Account Information</strong>: Name, email address, password, and profile details when you create an account</li>
          <li><strong>Profile Information</strong>: Bio, profile image, location, and other optional profile details</li>
          <li><strong>Digital Card Preferences</strong>: Aesthetic preferences, style selections, and personalization settings</li>
          <li><strong>User Content</strong>: Items you save, bookmark, share, or create, including product recommendations, reviews, and contributed content</li>
          <li><strong>Contributor Information</strong>: If you join our Contributor program, additional information such as payment details for commission tracking</li>
          <li><strong>Communications</strong>: Messages, feedback, and correspondence you send to us</li>
        </ul>

        <h3>1.2 Information Automatically Collected</h3>
        <ul>
          <li><strong>Usage Data</strong>: Pages visited, features used, time spent on the Platform, search queries, and interaction patterns</li>
          <li><strong>Device Information</strong>: Browser type, operating system, device type, IP address, and unique device identifiers</li>
          <li><strong>Location Data</strong>: Approximate geographic location based on IP address (we do not collect precise geolocation without your consent)</li>
          <li><strong>Cookies and Similar Technologies</strong>: We use cookies, web beacons, and similar tracking technologies to enhance your experience and analyze usage patterns</li>
        </ul>

        <h3>1.3 Information from Third Parties</h3>
        <ul>
          <li><strong>Authentication Services</strong>: If you sign in using Google or other third-party authentication, we receive basic profile information from those services</li>
          <li><strong>Affiliate Partners</strong>: When you click on affiliate links, we may receive information about purchases to track commissions (but not your personal payment information)</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve Artifex and its features</li>
          <li>Personalize your experience and content recommendations through your Digital Card</li>
          <li>Process and track affiliate transactions and contributor commissions</li>
          <li>Communicate with you about updates, new features, curated content, and platform news</li>
          <li>Respond to your inquiries, feedback, and support requests</li>
          <li>Analyze usage patterns, optimize performance, and develop new features</li>
          <li>Protect the security, integrity, and safety of our Platform and users</li>
          <li>Comply with legal obligations and enforce our Terms of Use</li>
          <li>Conduct research and analytics to better understand user preferences and improve curation</li>
        </ul>

        <h2>3. Information Sharing and Disclosure</h2>
        <p>We may share your information in the following circumstances:</p>

        <h3>3.1 With Your Consent</h3>
        <p>We may share your information when you explicitly authorize us to do so.</p>

        <h3>3.2 Service Providers</h3>
        <p>We share information with third-party service providers who perform services on our behalf, such as hosting, analytics, email delivery, and payment processing. These providers are contractually obligated to protect your information and use it only for the purposes we specify.</p>

        <h3>3.3 Affiliate and Commerce Partners</h3>
        <p>When you click on affiliate links or make purchases through the Platform, we share limited information (such as click data and anonymized purchase data) with our affiliate partners to track commissions and measure performance. We do not share your personal payment information with these partners.</p>

        <h3>3.4 Legal Requirements</h3>
        <p>We may disclose your information if required to do so by law, or in response to valid requests by public authorities (such as a court order or subpoena), or to protect our rights, privacy, safety, or property, or that of our users or the public.</p>

        <h3>3.5 Business Transfers</h3>
        <p>In the event of a merger, acquisition, reorganization, sale of assets, or bankruptcy, your information may be transferred as part of that transaction. We will provide notice before your information is transferred and becomes subject to a different privacy policy.</p>

        <h3>3.6 Aggregated and De-Identified Information</h3>
        <p>We may share aggregated, anonymized, or de-identified information that cannot reasonably be used to identify you for research, marketing, analytics, or other purposes.</p>

        <p><strong>We do not sell your personal information to third parties.</strong></p>

        <h2>4. Your Rights and Choices</h2>
        <p>You have the following rights regarding your information:</p>
        <ul>
          <li><strong>Access and Portability</strong>: You can access and download your personal information from your account settings</li>
          <li><strong>Correction</strong>: You can update or correct your account and profile information at any time</li>
          <li><strong>Deletion</strong>: You can request deletion of your account and associated personal information by contacting us at hello@a-r-t-i-f-e-x.com</li>
          <li><strong>Digital Card Preferences</strong>: You can adjust your Digital Card preferences and personalization settings at any time</li>
          <li><strong>Communications</strong>: You can opt out of promotional emails by clicking the unsubscribe link in any promotional message or by adjusting your account settings</li>
          <li><strong>Cookies</strong>: You can control cookies through your browser settings, though this may limit certain Platform functionality</li>
        </ul>

        <p>Depending on your location, you may have additional rights under applicable data protection laws, including the GDPR (for EU residents) or CCPA (for California residents). To exercise these rights, please contact us at hello@a-r-t-i-f-e-x.com.</p>

        <h2>5. Data Security</h2>
        <p>We implement industry-standard security measures to protect your personal information from unauthorized access, use, alteration, and disclosure. These measures include encryption, secure server infrastructure, and access controls. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>

        <h2>6. Data Retention</h2>
        <p>We retain your personal information for as long as necessary to provide you with the Platform, comply with legal obligations, resolve disputes, and enforce our agreements. When you delete your account, we will delete or anonymize your personal information, though some information may be retained in backup systems for a limited period or as required by law.</p>

        <h2>7. International Data Transfers</h2>
        <p>Artifex is based in the United States. Your information may be transferred to, stored, and processed in the United States or other countries where our service providers operate. These jurisdictions may have different data protection laws than your country. By using Artifex, you consent to the transfer of your information to these jurisdictions.</p>

        <h2>8. Children's Privacy</h2>
        <p>Artifex is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete that information promptly. If you believe we have collected information from a child under 13, please contact us at hello@a-r-t-i-f-e-x.com.</p>

        <h2>9. Third-Party Links and Services</h2>
        <p>Artifex may contain links to third-party websites, services, and products. This Privacy Policy does not apply to those third-party sites or services. We encourage you to review the privacy policies of any third-party sites you visit or services you use.</p>

        <h2>10. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated Privacy Policy on the Platform and updating the "Effective Date" above. Your continued use of Artifex after such changes constitutes your acceptance of the updated Privacy Policy.</p>

        <h2>11. Contact Us</h2>
        <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:</p>
        <p><strong>Email</strong>: hello@a-r-t-i-f-e-x.com</p>
        <p><strong>Mirador Pacific</strong><br />Artifex Privacy Team</p>
        
        <div className="info-links">
          <Link to="/about">About</Link>
          <Link to="/help">Help</Link>
          <Link to="/terms">Terms</Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;